import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Stack,
  alpha,
  Paper,
  TextField,
} from '@mui/material';
import { Warning as WarningIcon, Delete as DeleteIcon } from '@mui/icons-material';

const DS = {
  colors: {
    background: {
      primary: '#0f1419',
      secondary: '#1a1f2e',
    },
    text: {
      primary: '#ffffff',
      secondary: 'rgba(255, 255, 255, 0.7)',
      tertiary: 'rgba(255, 255, 255, 0.5)',
    },
    status: {
      success: '#00ff88',
      error: '#ff4757',
      warning: '#ffa500',
      info: '#00d4ff',
    },
  },
};

const ConfirmDialog = ({
  open,
  title,
  message,
  subMessage,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning',
  onConfirm,
  onCancel,
  loading = false,
  requiresTypedConfirmation = false,
}) => {
  const [typedValue, setTypedValue] = useState('');

  const severityConfig = {
    warning: {
      color: '#ffa500',
      bgColor: 'rgba(255, 165, 0, 0.08)',
      borderColor: 'rgba(255, 165, 0, 0.2)',
      icon: <WarningIcon sx={{ fontSize: 28 }} />,
      buttonColor: '#ffa500',
    },
    error: {
      color: '#ff4757',
      bgColor: 'rgba(255, 71, 87, 0.08)',
      borderColor: 'rgba(255, 71, 87, 0.2)',
      icon: <DeleteIcon sx={{ fontSize: 28 }} />,
      buttonColor: '#ff4757',
    },
    info: {
      color: '#00d4ff',
      bgColor: 'rgba(0, 212, 255, 0.08)',
      borderColor: 'rgba(0, 212, 255, 0.2)',
      icon: <WarningIcon sx={{ fontSize: 28 }} />,
      buttonColor: '#00d4ff',
    },
  };

  const config = severityConfig[severity] || severityConfig.warning;
  const isConfirmationValid = !requiresTypedConfirmation || typedValue.toLowerCase() === 'delete';

  const handleConfirm = () => {
    onConfirm();
    setTypedValue('');
  };

  const handleCancel = () => {
    onCancel();
    setTypedValue('');
  };

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'rgba(15, 20, 25, 0.98)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.6)',
          borderRadius: 1.5,
        },
      }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: config.bgColor,
          borderBottom: `1px solid ${config.borderColor}`,
          pb: 2.5,
          pt: 2.5,
          px: 3,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="flex-start">
          <Box
            sx={{
              color: config.color,
              display: 'flex',
              alignItems: 'center',
              mt: 0.25,
            }}
          >
            {config.icon}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: DS.colors.text.primary,
                fontWeight: 700,
                fontSize: '16px',
                mb: 0.5,
                letterSpacing: '-0.3px',
              }}
            >
              {title}
            </Typography>
            {subMessage && (
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '12px',
                  fontWeight: 500,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                {subMessage}
              </Typography>
            )}
          </Box>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ pt: 3, pb: 3, px: 3 }}>
        <Stack spacing={2.5}>
          <Paper
            sx={{
              bgcolor: config.bgColor,
              border: `1px solid ${config.borderColor}`,
              borderRadius: 1,
              p: 2.5,
            }}
          >
            <Typography
              sx={{
                color: 'rgba(255, 255, 255, 0.85)',
                fontSize: '13px',
                lineHeight: 1.6,
                fontWeight: 400,
              }}
            >
              {message}
            </Typography>
          </Paper>

          {requiresTypedConfirmation && (
            <Box>
              <Typography
                sx={{
                  color: 'rgba(255, 255, 255, 0.6)',
                  mb: 1.5,
                  fontSize: '12px',
                  fontWeight: 600,
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                }}
              >
                Type <Box component="span" sx={{ color: '#fff', fontWeight: 700 }}>delete</Box> to confirm
              </Typography>
              <TextField
                fullWidth
                size="small"
                placeholder='Type "delete"'
                value={typedValue}
                onChange={(e) => setTypedValue(e.target.value)}
                autoFocus
                disabled={loading}
                sx={{
                  '& .MuiInputBase-root': {
                    bgcolor: 'rgba(26, 31, 46, 0.6)',
                    borderRadius: 0.75,
                    fontSize: '13px',
                    color: '#fff',
                    border: `1px solid rgba(255, 255, 255, 0.12)`,
                    transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
                  },
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': {
                      borderColor: 'rgba(255, 255, 255, 0.2)',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: config.color,
                      boxShadow: `0 0 0 2px ${alpha(config.color, 0.1)}`,
                    },
                  },
                  '& .MuiInputBase-input::placeholder': {
                    color: 'rgba(255, 255, 255, 0.3)',
                    opacity: 1,
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          bgcolor: 'rgba(15, 20, 25, 0.6)',
          borderTop: '1px solid rgba(255, 255, 255, 0.06)',
          p: 2.5,
          gap: 1,
        }}
      >
        <Button
          onClick={handleCancel}
          disabled={loading}
          sx={{
            color: 'rgba(255, 255, 255, 0.7)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 0.75,
            fontWeight: 600,
            fontSize: '12px',
            textTransform: 'none',
            px: 2,
            py: 1,
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              borderColor: 'rgba(255, 255, 255, 0.2)',
              bgcolor: 'rgba(255, 255, 255, 0.05)',
            },
          }}
          variant="outlined"
        >
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          disabled={loading || !isConfirmationValid}
          variant="contained"
          sx={{
            bgcolor: isConfirmationValid ? config.buttonColor : 'rgba(255, 255, 255, 0.2)',
            color: severity === 'error' ? '#fff' : '#000',
            fontWeight: 600,
            fontSize: '12px',
            textTransform: 'none',
            borderRadius: 0.75,
            px: 2.5,
            py: 1,
            transition: 'all 150ms cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              opacity: isConfirmationValid ? 0.85 : 1,
            },
            '&:disabled': {
              opacity: 0.5,
              cursor: 'not-allowed',
            },
          }}
        >
          {loading ? 'Processing...' : confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
